import React, { memo, useCallback } from 'react';
import { Button, Modal, Spin } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAssetsQuery } from '@hooks/queries/assets';
import { useDispatch } from 'react-redux';
import { toggleUploadAssetModal } from '@redux/actions/modalAction';
import { ReactComponent as CrossSvg } from '@assets/icons/cross.svg';
import { ReactComponent as EmptySvg } from '@assets/icons/empty-campaigns.svg';
import { ReactComponent as DemoSvg } from '@assets/icons/demo-campaigns.svg';

import './CollaborateOnAnAssetModal.less';

type CollaborateOnAnAssetModalProps = {
  visible: boolean;
  workspaceId: string;
  campaignId?: string;
  setVisible: (v: boolean) => void;
};

export default memo(function CollaborateOnAnAssetModal({
  visible,
  workspaceId,
  campaignId,
  setVisible
}: CollaborateOnAnAssetModalProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const startTour = searchParams.get('startTour') ?? 'create-task-from-comment';

  const { data: demoData, isLoading } = useAssetsQuery(
    {
      isFolder: false,
      workspaceId,
      isDemo: true,
      flatten: true,
      limit: 1
    },
    { enabled: visible && !!workspaceId }
  );

  const demoAssetId = demoData?.pages?.[0].edges.find((it) => {
    const v = it.node.asset?.versions[0];
    return !!v;
  })?.node.id;

  const onCloseModal = useCallback(() => {
    setVisible(false);
  }, [setVisible]);

  const onOwnAssetClick = useCallback(() => {
    dispatch(
      toggleUploadAssetModal({
        visible: true,
        postAction: 'redirect-to-mv',
        workspaceId,
        campaignId
      })
    );
    onCloseModal();
  }, [onCloseModal, dispatch, workspaceId, campaignId]);

  const onDemoAssetClick = useCallback(() => {
    if (!demoAssetId) return;

    navigate(
      `/media-viewer/${demoAssetId}/${demoAssetId}?startTour=${startTour}`
    );
  }, [navigate, startTour, demoAssetId]);

  return (
    <Modal
      open={visible}
      footer={null}
      destroyOnClose
      centered={true}
      closeIcon={<CrossSvg />}
      width={740}
      onCancel={onCloseModal}
    >
      {isLoading && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '234px'
          }}
        >
          <Spin size="large" />
        </div>
      )}
      {!isLoading && (
        <div className="modal_container">
          <h2
            className="modal_container__title"
            style={{
              fontSize: 22,
              marginBottom: '0.5em',
              justifyContent: 'center',
              textAlign: 'center',
              width: '485px',
              margin: '0 auto 0.5em'
            }}
          >
            Upload a media
          </h2>
          <p
            className="modal_container__subtitle"
            style={{
              textAlign: 'center',
              width: '404px',
              margin: '0 auto 32px'
            }}
          >
            Choose what you want to do first.
          </p>

          <div className="modal_container__create-task-in-campaign">
            <div className="item-block">
              <div className="item-block-wrapper">
                <div className="item-block-wrapper__icon">
                  <EmptySvg />
                </div>
                <div className="item-block-wrapper-copies">
                  <div className="item-block-wrapper-copies__title">
                    Upload media
                  </div>
                  <div className="item-block-wrapper-copies__description">
                    Upload a media to start collaborating
                  </div>
                  <Button type="primary" onClick={onOwnAssetClick}>
                    Upload
                  </Button>
                </div>
              </div>
            </div>
            <div className="item-block">
              <div className="item-block-wrapper">
                <div className="item-block-wrapper__icon">
                  <DemoSvg />
                </div>
                <div className="item-block-wrapper-copies">
                  <div className="item-block-wrapper-copies__title">
                    Use demo media
                  </div>
                  <div className="item-block-wrapper-copies__description">
                    Learn how to collaborate with a demo media
                  </div>
                  <Button
                    size="middle"
                    type="primary"
                    onClick={onDemoAssetClick}
                    disabled={!demoAssetId}
                  >
                    Continue with demo
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
});
